import { SectionHeader, TextBadge } from '@/components/ui';
import { MaskedText } from '@/components/ui/MaskedText';
import { cn } from '@/lib/cn';
import { IconBuilding, IconDislike, IconLike, IconLocation } from '@repo/svgs';

interface TenancyLengthSectionProps {
  buildingAddress: string;
  neighborhoodName?: string;
  isBlurRequired: boolean;
  tenancyData: any | null;
}

function toYears(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Heuristic: values > 10 are very likely "months" (e.g. 22 months). Convert to years.
  return n > 10 ? n / 12 : n;
}

function formatYears(years: number | null): string {
  if (years === null) return '-';
  return `${years.toFixed(1)} years`;
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

function BarRow({
  icon,
  label,
  years,
  widthPct,
  barClassName,
  isBlurRequired,
}: {
  icon: React.ReactNode;
  label: string;
  years: number | null;
  widthPct: number;
  barClassName: string;
  isBlurRequired: boolean;
}) {
  return (
    <div className="grid grid-cols-[28px_1fr] gap-4">
      <div>{icon}</div>
      <div className="min-w-0">
        {/* Bar */}
        <div
          className={cn(
            'my-2 h-2 w-full rounded-full',
            isBlurRequired ? 'bg-[#64748B]' : years === null || years === 0 ? 'bg-secondary' : '',
          )}
        >
          {!isBlurRequired && years !== null ? (
            <div
              className={cn('h-2 rounded-full', barClassName)}
              style={{ width: `${Math.round(widthPct)}%` }}
            />
          ) : null}
        </div>

        {/* Values */}
        <div>
          <div className="text-body-2-high text-primary">
            {isBlurRequired ? <MaskedText className="w-16 h-4" /> : formatYears(years)}
          </div>
          <div className="text-caption text-secondary pt-1">{label}</div>
        </div>
      </div>
    </div>
  );
}

export function TenancyLengthSection({
  buildingAddress,
  neighborhoodName = '',
  isBlurRequired,
  tenancyData,
}: TenancyLengthSectionProps) {
  const buildingYears = toYears(tenancyData?.buildingTenancyLength);
  const neighborhoodYears = toYears(tenancyData?.neighborhoodTenancyLength);

  const maxYears = Math.max(buildingYears ?? 0, neighborhoodYears ?? 0);
  const buildingWidth = maxYears > 0 ? clamp01((buildingYears ?? 0) / maxYears) * 100 : 0;
  const neighborhoodWidth = maxYears > 0 ? clamp01((neighborhoodYears ?? 0) / maxYears) * 100 : 0;

  const showInsight = !isBlurRequired && buildingYears !== null && neighborhoodYears !== null;
  const isPositive = showInsight ? buildingYears >= neighborhoodYears : null;
  const insightText = showInsight
    ? `The average tenancy of ${buildingAddress} renters is ${
        isPositive ? 'longer' : 'shorter'
      } than other buildings in ${neighborhoodName}.`
    : '';

  return (
    <div className="w-full">
      <SectionHeader
        title={`Tenancy length at ${buildingAddress}`}
        subtitleText="Openigloo analysis"
      />

      <div className="md:px-4 px-2">
        {/* Insight banner */}
        {isBlurRequired ? (
          <div className="mt-2 rounded-2xl bg-secondary p-4">
            <MaskedText className="h-4 w-3/4" />
          </div>
        ) : showInsight ? (
          <TextBadge
            className="mt-2"
            variant={isPositive ? 'positive' : 'negative'}
            icon={
              isPositive ? (
                <IconLike className="w-4 h-4 icon-success shrink-0" aria-hidden />
              ) : (
                <IconDislike className="w-4 h-4 icon-alert shrink-0" aria-hidden />
              )
            }
          >
            {insightText}
          </TextBadge>
        ) : null}

        {/* Rows */}
        <div className="mt-4 space-y-4">
          <BarRow
            icon={<IconBuilding className="w-6 h-6 icon-secondary" aria-hidden />}
            label={buildingAddress}
            years={buildingYears}
            widthPct={buildingWidth}
            barClassName="bg-accent"
            isBlurRequired={isBlurRequired}
          />
          <BarRow
            icon={<IconLocation className="w-6 h-6 icon-secondary" aria-hidden />}
            label={neighborhoodName}
            years={neighborhoodYears}
            widthPct={neighborhoodWidth}
            barClassName="bg-[#3B2AD8]"
            isBlurRequired={isBlurRequired}
          />
        </div>
      </div>
    </div>
  );
}
